import { useMutation, useQuery } from '@apollo/client';
import {
  AppContext,
  DeleteModal,
  Icon,
  SearchFilter,
  getTranslatedText,
  AccessControl,
  BackgroundIcon,
  toast,
  ListInfoBanner,
  Tag,
  ALL_MODULES,
  EventEmitter,
  FeatureGuide,
  useUserMSPCheckHook,
  getDateOperators,
  useIsAuthorisedHook,
  useIntl,
  useSuperListHook,
  ALL_TABLES
} from '@nile/components';
import {
  GET_ENTITY_PROPERTIES,
  GET_STATUS_CONFIGURATION,
  GET_TICKET_LISTVIEW_META
} from '@nile/graphql';
import {
  SOFT_DELETE_PROJECTS,
  RESTORE_PROJECTS,
  HARD_DELETE_PROJECTS
} from '@nile/projects/src/Graphql';
import { PROJECT_VIEW_IDS } from '@nile/projects/src/Constants/index';
import {
  DeleteConfirmation,
  MergeTicketSheet
} from '@nile/mangifera/src/Components/Sheets';
import ListViewColumns from '@nile/mangifera/src/Containers/SheetContainers/ListViewColumns';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { DELETE_TICKET_VIEW } from '@nile/mangifera/src/Graphql/Mutations/ticketview';
import {
  Button,
  ButtonGroup,
  Heading,
  Popover,
  Badge
} from '@superops/ui-design-system';
import { DotsVertical } from '@styled-icons/boxicons-regular';
import { CheckCircle } from '@styled-icons/boxicons-solid/CheckCircle';
import { UserCheck } from '@styled-icons/fa-solid';
import { Settings } from '@styled-icons/material/Settings';
import { Spam } from '@styled-icons/remix-fill';
import React, { useReducer, useContext, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';

import styled from 'styled-components';
import {
  checkDynamicQuery,
  fetchValues,
  generateOptions,
  getAsyncOptions,
  getDefaultValue,
  getDynamicQuery,
  getKeyName,
  getMethodName,
  getVariables
} from '@nile/projects/src/Pebbles/ProjectPages';
import { ProjectListingHeader } from '@nile/projects/src/Components/Headers';
import { PlusCircle } from '@styled-icons/fa-solid/PlusCircle';
import shortid from 'shortid';
import { FormattedMessage, injectIntl } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import { Tooltip } from 'react-clone-tippy';
import { SVGIcon } from '@superops/super-icons';
import EditProjectView from '@nile/projects/src/Components/ProjectView/EditProjectView';
import { DELETE_VIEW } from '@nile/graphql/src/Mutation/views';
import AdvancedProjectFilter from '@nile/projects/src/Components/ProjectView/AdvancedProjectFilter';
import { cloneDeep } from 'lodash';
import ProjectListColumns from './ProjectListColumns';

const StyledToolbar = styled.div`
  height: 90px;
  background: #ffffff;
  border-bottom: 1px solid #e7e7e7;
  padding: 20px;
  .f11 {
    flex: 1 1;
  }
  .ticketing-modify {
    display: flex;
    align-items: center;
  }
  .navMenu .Polaris-Button {
    padding: 0 10px !important;
  }
  .navMenu {
    margin-right: 20px !important;
  }
  .danger {
    color: #e02020;
  }
`;

const MoreActivator = styled.div`
  border-radius: 50%;
  cursor: pointer;
  background: #ececec;
  &:hover {
    background: #ececec;
  }
  &:focus {
    outline: none;
  }
`;

const Action = styled.div`
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  white-space: nowrap;
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

function MoreActions({
  onDeleteProjectView,
  isVisible,
  toggleEditProjectViewModal
}) {
  const [isPopoverVisible, togglePopover] = useReducer((s) => !s, false);

  const intl = useIntl();

  const popoverActivatorIcon = (
    <div className="ml8 flex cursor-pointer" onClick={togglePopover}>
      <Badge icon={<SVGIcon name="Actions" size="md" />} radial size="medium" />
    </div>
  );

  if (!isVisible) return null;

  return (
    <Popover
      active={isPopoverVisible}
      activator={popoverActivatorIcon}
      preferredAlignment="left"
      onClose={togglePopover}
    >
      <div className="p8 min-width-150" title="More actions">
        <AccessControl
          roleToCheck="projectViews.edit"
          render={
            <Action
              className="flex center"
              onClick={() => {
                togglePopover();
                toggleEditProjectViewModal();
              }}
            >
              <div className="mr-10">
                <SVGIcon name="Edit" />
              </div>
              <FormattedMessage id="taskView.modifyView" />
            </Action>
          }
        />

        <AccessControl
          roleToCheck="projectViews.delete"
          render={
            <Action className="flex center">
              <DeleteModal
                modalActivator={
                  <div className="pointer flex center">
                    <span className="mr10">
                      <SVGIcon name="Delete" />
                    </span>
                    <FormattedMessage id="common.delete" />
                  </div>
                }
                description={getTranslatedText(
                  intl,
                  'taskView.delete.confirmation'
                )}
                handleDeleteConfirmation={() => {
                  togglePopover();
                  onDeleteProjectView();
                }}
                title={getTranslatedText(intl, 'taskView.deleteView')}
              />
            </Action>
          }
        />
      </div>
    </Popover>
  );
}

function ProjectInfoToolbar(props) {
  const { context, activeView, params, setRecords, records, viewType } = props;

  const [isEditProjectViewVisible, toggleEditProjectViewModal] = useReducer(
    (s) => !s,
    false
  );

  const intl = useIntl();

  const isEditAuthorised = useIsAuthorisedHook('projectViews.edit');
  const isDeleteAuthorised = useIsAuthorisedHook('projectViews.delete');

  useEffect(() => {
    setRecords([]);
  }, [activeView?.id]);

  const [deleteProjectView] = useMutation(DELETE_VIEW);

  const deleteview = () => {
    deleteProjectView({
      variables: {
        id: activeView.id,
        input: {
          module: 'PROJECT'
        }
      }
    }).then((res) => {
      if (res?.data?.deleteView) {
        EventEmitter.dispatch('deleteProjectView');
      }
    });
  };

  return (
    <div className="flex f11">
      {isEditProjectViewVisible && (
        <EditProjectView
          onClose={toggleEditProjectViewModal}
          id={activeView.id}
          viewType={viewType}
        />
      )}
      <Heading element="h1">
        <div className="flex center">
          <div className="flex center">
            <div className="mr8 navMenu" onClick={props.toggleNavMenu}>
              {props.navMenu}
            </div>
            {activeView.name}
            {activeView.shared ? (
              <Tooltip
                title={getTranslatedText(intl, 'common.shared.view')}
                delay={200}
                duration={250}
                position="bottom"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
              >
                <div className="fx center mr8 ml-16">
                  <SVGIcon name="GlobeSharedView" size="md" color="#6e7175" />
                </div>
              </Tooltip>
            ) : (
              ''
            )}
          </div>
        </div>
      </Heading>
      <MoreActions
        intl={intl}
        isVisible={
          activeView.customView && (isEditAuthorised || isDeleteAuthorised)
        }
        onDeleteProjectView={deleteview}
        toggleEditProjectViewModal={toggleEditProjectViewModal}
      />
    </div>
  );
}

function ProjectActionToolbar(props) {
  const { context, activeView, params, setRecords, records } = props;

  const intl = useIntl();

  const [softDeleteProjects] = useMutation(SOFT_DELETE_PROJECTS, {
    onCompleted({ softDeleteProjects }) {
      if (softDeleteProjects) {
        toast('success', 'toast.projects.bulk.softdelete.success');
      }
    }
  });
  const [hardDeleteProjects] = useMutation(HARD_DELETE_PROJECTS, {
    onCompleted({ hardDeleteProjects }) {
      if (hardDeleteProjects) {
        toast('success', 'toast.projects.bulk.harddelete.success');
      }
    }
  });
  const [restoreProjects] = useMutation(RESTORE_PROJECTS, {
    onCompleted({ restoreProjects }) {
      if (restoreProjects) {
        toast('success', 'toast.projects.bulk.restore.success');
      }
    }
  });

  useEffect(() => {
    context.setViewConditions(null);
  }, [activeView]);

  const modalActivator = (
    <Button
      customClassName="action-button p-0"
      icon={<SVGIcon name="MoveTrash" color="#ED0050" />}
    >
      <div className="danger">
        <FormattedMessage id="project.view.bulk.delete.title" />
      </div>
    </Button>
  );

  return (
    <div className="flex f11">
      <div className="mlauto">
        {!activeView?.systemViewProperty ? (
          <ButtonGroup>
            <Tag>{`${records?.length} ${getTranslatedText(
              intl,
              'ticket.details.selected'
            )}`}</Tag>
            {/* <DeleteModal
              modalActivator={modalActivator}
              description={getTranslatedText(
                intl,
                'project.view.bulk.delete.description'
              )}
              handleDeleteConfirmation={() =>
                softDeleteProjects({
                  variables: {
                    ids: context.records
                  }
                }).then((res) => context.setRecords([]))
              }
              title={getTranslatedText(intl, 'project.view.bulk.delete.title')}
            /> */}
            <Button
              icon={<SVGIcon name="MoveTrash" color="#ED0050" />}
              onClick={() =>
                softDeleteProjects({
                  variables: {
                    ids: records
                  }
                }).then((res) => {
                  setRecords([]);
                  EventEmitter.dispatch('refreshProjectViewCount', [
                    activeView.id,
                    PROJECT_VIEW_IDS.trash
                  ]);
                })
              }
            >
              <div className="danger">
                <FormattedMessage id="project.view.bulk.delete.title" />
              </div>
            </Button>
          </ButtonGroup>
        ) : (
          <ButtonGroup>
            <Tag>{`${records?.length} ${getTranslatedText(
              intl,
              'ticket.details.selected'
            )}`}</Tag>
            <Button
              onClick={() =>
                restoreProjects({
                  variables: { ids: records }
                }).then((res) => {
                  setRecords([]);
                  EventEmitter.dispatch('refreshProjectViewCount', [
                    activeView.id
                  ]);
                })
              }
              id="restoreBtn"
              icon={<SVGIcon name="Restore" color="#323232" size="sm" />}
            >
              <FormattedMessage id="project.view.bulk.restore.button" />
            </Button>
            <Button
              onClick={() =>
                hardDeleteProjects({
                  variables: { ids: records }
                }).then((res) => {
                  setRecords([]);
                  EventEmitter.dispatch('refreshProjectViewCount', [
                    activeView.id
                  ]);
                })
              }
              id="deleteBtn"
              icon={<SVGIcon name="Delete" color="#E02020" size="sm" />}
            >
              <span className="danger">
                <FormattedMessage id="project.view.bulk.delete.button" />
              </span>
            </Button>
          </ButtonGroup>
        )}
      </div>
    </div>
  );
}

function ProjectToolbar(props) {
  /*  Default active view will be all, it will served from server or constant, here it is temp fix*/
  const { match, views, viewType, setViewType } = props;

  const isMSP = useUserMSPCheckHook();

  const intl = useIntl();

  // const context = useContext(TicketContext);
  const tabContext = useContext(TabContext);
  const appContext = useContext(AppContext);
  const { accountType } = appContext;
  const activeView = tabContext.activeView
    ? tabContext.activeView
    : { name: 'Active Projects', id: '6' };

  const isListView = viewType === 'list';
  const {
    records: superListRecords,
    setRecords: setSuperListRecords,
    updateAdvanceFilterCondition: updateSuperListAdvanceFilterCondition,
    advanceFilterCondition: superListAdvanceFilterCondition,
    tableCondition: superListTableCondition,
    resetFilters: resetSuperListFilters
  } = useSuperListHook({
    tableName: ALL_TABLES.projectTable,
    viewId: activeView.id
  });

  const setRecords = isListView ? setSuperListRecords : tabContext.setRecords;
  const records = isListView
    ? superListRecords?.map((item) => item.projectId) || [] //only return ids in array
    : tabContext.records || [];

  const updateCondition = (condition = null) => {
    tabContext.setProjectListInfo((prev) => ({
      ...prev,
      page: 1,
      condition: condition
    }));
  };

  const DEFAULT_PROJECT_CONDITION = {
    joinOperator: 'AND',
    operands: [{ attribute: 'projectId', operator: 'isNotEmpty', value: '' }]
  };

  const projectAdvanceFilterCondition = isListView
    ? superListAdvanceFilterCondition || superListTableCondition
    : tabContext.viewConditions;

  const updateAdvancedConditions = (condition = []) => {
    let complexCondition = cloneDeep(projectAdvanceFilterCondition);
    const listCondition =
      complexCondition?.operands?.[0] || DEFAULT_PROJECT_CONDITION;
    complexCondition = {
      joinOperator: 'AND',
      operands: [listCondition].concat(condition || [])
    };
    if (isListView) {
      updateSuperListAdvanceFilterCondition(complexCondition);
    } else {
      // to show ResetFilter button
      tabContext.setViewConditions(complexCondition);

      //trigger api after applying filter
      tabContext.setProjectListInfo((prev) => ({
        ...prev,
        page: 1,
        condition: complexCondition
      }));
    }
  };

  const resetFilters = () => {
    if (isListView) {
      resetSuperListFilters();
    } else {
      // to hide ResetFilter button
      tabContext.setViewConditions(null);

      //trigger api after reset filter
      tabContext.setProjectListInfo((prev) => ({
        ...prev,
        page: 1,
        condition: null
      }));
    }
  };

  return (
    <StyledToolbar
      className={`fx actr ${records?.length > 0 ? 'frow-reverse' : ''}`}
    >
      {records?.length > 0 && (
        <>
          <ProjectActionToolbar
            intl={intl}
            params={match.params}
            activeView={activeView}
            context={tabContext}
            setRecords={setRecords}
            records={records}
            appContext={appContext}
            location={props.location}
          />
          {!!activeView?.systemViewProperty && (
            <>
              <div className="flex jend f11">
                <ListInfoBanner message="project.view.trash.information" />
              </div>
            </>
          )}
        </>
      )}
      <>
        <ProjectInfoToolbar
          intl={intl}
          toggleNavMenu={props.toggleNavMenu}
          navMenu={props.navMenu}
          params={match.params}
          activeView={activeView}
          setRecords={setRecords}
          location={props.location}
          viewType={viewType}
        />
        <div className="fx actr g10">
          {activeView?.systemViewProperty !== 'TRASH_PROJECT' &&
            records?.length === 0 &&
            isMSP && (
              <div className="mr20 fx center">
                <FeatureGuide featureName="projectmanagement" video onlyIcon />
              </div>
            )}
          {projectAdvanceFilterCondition && (
            <div className="ml8 mr8 reset">
              <Button id="resetFilterTask" plain onClick={resetFilters}>
                <FormattedMessage id="ticket.details.resetFilters" />
              </Button>
            </div>
          )}
          {isMSP && records?.length === 0 && (
            <div className="fx frow center">
              <span className="fx mr-10" id="project_view_type">
                <ButtonGroup segmented>
                  <Tooltip
                    title={getTranslatedText(intl, 'ticket.listView.tooltip')}
                    delay={200}
                    duration={250}
                    position="bottom"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                  >
                    <Button
                      outline={viewType === 'list'}
                      highlighted={viewType === 'list'}
                      onClick={() => setViewType('list')}
                      icon={<SVGIcon name="ListView" size="sm" />}
                    />
                  </Tooltip>
                  <Tooltip
                    title={getTranslatedText(
                      intl,
                      'admin.integrations.stripe.payments.cardSelect'
                    )}
                    delay={200}
                    duration={250}
                    position="bottom"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                  >
                    <Button
                      outline={viewType === 'card'}
                      highlighted={viewType === 'card'}
                      onClick={() => setViewType('card')}
                      icon={<SVGIcon name="CustomStatus" size="sm" />}
                    />
                  </Tooltip>
                </ButtonGroup>
              </span>
              {isListView && (
                <ProjectListColumns
                  viewId={activeView?.id}
                  module={ALL_MODULES.project}
                />
              )}
            </div>
          )}
          {isMSP &&
            records?.length === 0 &&
            ((activeView?.systemViewProperty && isListView) ||
              !activeView?.systemViewProperty) && (
              <>
                <AdvancedProjectFilter
                  updateCondition={updateAdvancedConditions}
                  conditions={projectAdvanceFilterCondition}
                />
              </>
            )}
          {!activeView?.systemViewProperty &&
            records?.length === 0 &&
            accountType !== 'CLIENT' && (
              <>
                {/* <SearchFilter
                  entity="Project"
                  personalization={tabContext.personalization}
                  updateCondition={updateCondition}
                  currentViewConditions={tabContext.projectListInfo.condition}
                  getDefaultValue={getDefaultValue}
                  getDynamicQuery={getDynamicQuery}
                  checkDynamicQuery={checkDynamicQuery}
                  generateOptions={generateOptions}
                  getMethodName={getMethodName}
                  getKeyName={getKeyName}
                  fetchValues={fetchValues}
                  getAsyncOptions={getAsyncOptions}
                  getDateOperators={getDateOperators.bind(this, intl)}
                  getVariables={getVariables}
                /> */}
                <AccessControl
                  roleToCheck="project.add"
                  render={
                    <Button
                      primary
                      id="createProjectBtn"
                      onClick={() => {
                        tabContext.openTab({
                          module: ALL_MODULES.projectCreation
                        });
                      }}
                      icon={<SVGIcon name="Add" color="#fff" />}
                    >
                      <FormattedMessage id="common.create" />
                    </Button>
                  }
                />
              </>
            )}
          {activeView?.systemViewProperty && records?.length === 0 && (
            <>
              <div className="flex jend f21">
                <ListInfoBanner message="project.view.trash.information" />
              </div>
            </>
          )}
        </div>
      </>
    </StyledToolbar>
  );
}
export default injectIntl(withRouter(ProjectToolbar));
